/* eslint-disable jsx-a11y/interactive-supports-focus */
import classnames from "classnames";
import parse from "html-react-parser";
import { useRouter } from "next/router";
import React, { useEffect, useRef, useState, forwardRef } from "react";

// Components
import { Badge, BadgeType } from "@JohnLewisPartnership/jl-design-system/dist/components/badge/Badge";
import { Message } from "@JohnLewisPartnership/jl-design-system/dist/components/message/Message";
import { UnderlinedButton } from "@JohnLewisPartnership/jl-design-system/dist/elements/button/Button";
import { TickOutlined as TickIcon } from "@JohnLewisPartnership/jl-design-system/dist/components/icons/TickOutlined";
import Link from "next/link";
import DecisionTreeIcon from "../decision-tree-icon/DecisionTreeIcon";

// Utils
import { dispatchNewEvent, updateDataLayer } from "../../utils/analytics/trigger/analyticsTrigger";
import getSolutionById from "../../utils/data/getSolutionById";

// Styles
import NewLabel from "../new-label/NewLabel";
import styles from "./decision-tree-node.module.scss";

export const DecisionTreeNodeView = forwardRef(function DecisionTreeNodeView(
  {
    solution: {
      image,
      infoBox,
      question,
      experimentQuestion,
      diagnosticResponses,
      childId,
      parentIds,
      diagnosticResponsesHaveIcons,
    },
    className,
    index,
  },
  ref,
) {
  const router = useRouter();
  const [storedResponse, setStoredResponse] = useState(null);
  const selectedResponse = childId && diagnosticResponses.find(({ id }) => id === childId);

  const editButtonRef = useRef(null);

  useEffect(() => {
    if (!selectedResponse && editButtonRef.current) {
      editButtonRef.current.blur();
    }
    if (selectedResponse) {
      document.activeElement?.blur();
    }
  }, [selectedResponse]);
  useEffect(() => {
    if (selectedResponse && selectedResponse !== storedResponse) {
      setStoredResponse(selectedResponse);
    }
  }, [childId]);

  const handleOptionSelected = solutionId => {
    const parentsStr = parentIds.filter(str => str !== "contact-us").join("|");
    const fullJourney = `${parentsStr}${parentsStr !== "" ? "|" : ""}${solutionId}`;

    const dataLayer = window.jlData;
    dataLayer.page.decisionTree.refinement = fullJourney;
    updateDataLayer("dataStateChange", dataLayer);
    const solution = getSolutionById(solutionId);

    if (solution) {
      dispatchNewEvent("dataStateChange", {
        action: "contactUsDecision",
        type: fullJourney,
        isSolution: solution.diagnosticResponses.length ? "0" : "1",
      });
    }
  };

  function getResponseText(responseText, experimentResponseText) {
    return experimentResponseText ? experimentResponseText : responseText;
  }

  const optionListItem = ({
    id,
    responseText,
    experimentResponseText,
    linkTo,
    url,
    openInNewTab,
    showNewLabel,
    showNewBadge,
  }) => (
    <li key={id} className={styles["decision-tree-node__option-list-item"]} data-test="decision-tree-item">
      <Link
        href={
          url || {
            pathname: router.pathname,
            query: {
              ...router.query,
              dt: !linkTo ? parentIds.slice(1).concat(id).join("-") : linkTo,
            },
          }
        }
        shallow
        scroll={false}
        className={classnames(styles["decision-tree-node__option"], {
          [styles["decision-tree-node__option--with-icon"]]: diagnosticResponsesHaveIcons,
        })}
        role="button"
        data-test="decision-tree-option"
        onClick={() => handleOptionSelected(id)}
        onKeyDown={() => handleOptionSelected(id)}
        target={openInNewTab ? "_blank" : "_self"}
      >
        {showNewLabel && <NewLabel className={classnames(styles["decision-tree-node__new-label"])} />}
        {diagnosticResponsesHaveIcons && (
          <DecisionTreeIcon id={id} className={styles["decision-tree-node__option-icon"]} />
        )}
        {showNewBadge && (
          <Badge className={styles["decision-tree-node__new-badge"]} label="New" badgeType={BadgeType.INFORMATION} />
        )}
        <span className={styles["decision-tree-node__label"]}>
          {showNewLabel && <NewLabel className={classnames(styles["decision-tree-node__new-label"])} />}
          {getResponseText(responseText, experimentResponseText)}
        </span>
      </Link>
    </li>
  );

  const optionList = () => {
    if (diagnosticResponses.length > 3 && diagnosticResponses.length <= 6) {
      return (
        <>
          <ul
            className={classnames(
              styles["decision-tree-node__option-list"],
              styles["decision-tree-node__option-list--not-last"],
            )}
          >
            {diagnosticResponses.slice(0, 3).map(optionListItem)}
          </ul>
          <ul className={styles["decision-tree-node__option-list"]}>
            {diagnosticResponses.slice(3).map(optionListItem)}
          </ul>
        </>
      );
    }
    return <ul className={styles["decision-tree-node__option-list"]}>{diagnosticResponses.map(optionListItem)}</ul>;
  };

  return (
    <article
      ref={ref}
      aria-label={
        selectedResponse
          ? `Your selected option group is ${
              storedResponse && getResponseText(storedResponse.responseText, storedResponse.experimentResponseText)
            } for step ${index + 1}`
          : `Select one of the options from the option group for step ${index + 1}`
      }
      className={classnames(
        styles["decision-tree-node"],
        {
          [styles["decision-tree-node--answered"]]: selectedResponse,
          [styles["decision-tree-node--unanswered"]]: !selectedResponse,
        },
        className,
      )}
    >
      <Link
        href={{
          pathname: router.pathname,
          query: {
            ...router.query,
            dt: parentIds.slice(1).join("-"),
          },
        }}
        shallow
        scroll={false}
      >
        <div aria-hidden={!selectedResponse} className={styles["decision-tree-node__answered"]}>
          <div className={styles["decision-tree-node__answered__left"]} aria-hidden="true">
            <TickIcon className={styles["decision-tree-node__icon"]} />
            <div className={styles["decision-tree-node__qa-holder"]}>
              <p className={styles["decision-tree-node__answer"]} data-test="decision-tree-answer">
                {storedResponse && getResponseText(storedResponse.responseText, storedResponse.experimentResponseText)}
              </p>
            </div>
          </div>
          <div className={styles["decision-tree-node__answered__right"]}>
            <UnderlinedButton
              ref={editButtonRef}
              className={styles["decision-tree-node__edit-button"]}
              aria-hidden={!selectedResponse}
              data-test="decision-tree-edit-button"
              role="button"
              aria-label={`Edit selected option: ${
                storedResponse && getResponseText(storedResponse.responseText, storedResponse.experimentResponseText)
              }`}
            >
              <span aria-hidden>Edit</span>
            </UnderlinedButton>
          </div>
        </div>
      </Link>

      <div aria-hidden={!!selectedResponse} className={styles["decision-tree-node__unanswered"]}>
        {image && (
          <img
            className={styles["decision-tree-node__image"]}
            src={`${image.src}?fmt=auto`}
            srcSet={`${image.src}?fmt=auto&wid=${image.width} ${image.width}w`}
            sizes="100vw"
            alt={image.alt}
          />
        )}
        {infoBox && (
          <Message
            className={styles["decision-tree-node__infobox"]}
            type="information"
            title={infoBox.title}
            body={parse(infoBox.message)}
            whiteBackground
            wideTab
          />
        )}
        <h3 className={styles["decision-tree-node__question"]}>{experimentQuestion ? experimentQuestion : question}</h3>
        {optionList()}
      </div>
    </article>
  );
});

export default DecisionTreeNodeView;
